import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { apiProcess } from 'app/config/axios-interceptor';
import { createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState: EntityState<any> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: {},
  viewing: false,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  previewAjustmentSubmission: [],
  previewAjustmentSubmissionLoading: false,
  previewAjustmentSubmissionError: null,
  previewAjustmentSubmissionSuccess: false,
  postAjustmentSubmissionLoading: false,
  postAjustmentSubmissionError: null,
  postAjustmentSubmissionSuccess: false,
  typeSelectbox: [],
  loadingTypeSelectbox: false,
  adjustmentConfigure: [],
  loadingAdjustmentConfigure: false,
  approveConfigure: [],
  loadingApproveConfigure: false,
  approveAdjustedYear: [],
  loadingApproveAdjustedYear: false,
  loadingCreateAdjustmentConfigure: false,
  createAdjustmentConfigureError: null,
};

const apiUrl = 'api/approve-configures';

// Actions

export const getEntities = createAsyncThunk(
  'approvalLevel/fetch_entity_list',
  async (ownerId: string | number) => {
    return apiProcess.get<any[]>(apiUrl, {
      params: {
        ownerId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'approvalLevel/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return apiProcess.get<any>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'approvalLevel/create_entity',
  async (entity: any, thunkAPI) => {
    const result = await apiProcess.post<any>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'approvalLevel/update_entity',
  async (entity: any, thunkAPI) => {
    const result = await apiProcess.put<any>(`${apiUrl}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const getTypeSelectboxApprovalLevel = createAsyncThunk(
  'approvalLevel/get_type_selectbox',
  async (thunkAPI) => {
    const result = await apiProcess.get<any>('api/enum-selectboxs/type-selectboxs');
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'approvalLevel/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await apiProcess.delete<any>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const previewAjustmentSubmission = createAsyncThunk(
  'approvalLevel/preview_ajustment_submission',
  async (itemsSelected: any, thunkAPI) => {
    const requestUrl = 'api/ajustments/preview-ajustment-submission';
    const result = await apiProcess.get<any>(requestUrl, {
      params: {
        ItemIds: itemsSelected
      },
      paramsSerializer: {
        indexes: null // array format: ItemIds=1&ItemIds=2 instead of ItemIds[]=1
      }
    });
    return result;
  },
);

export const postAjustmentSubmission = createAsyncThunk(
  'approvalLevel/post_ajustment_submission',
  async (params: { senderid: string; ownerid: string; planPeriodId: string; itemIds: [], year: number }, thunkAPI) => {
    const requestUrl = 'api/ajustments/submit-ajustment';
    const result = await apiProcess.post<any>(requestUrl, params);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const getAdjustmentConfigure = createAsyncThunk(
  'approvalLevel/get_adjustment_configure',
  async (params: { ownerid: string, planPeriodId: string }, thunkAPI) => {
    const requestUrl = 'api/adjustment-configure';
    const result = await apiProcess.get<any>(requestUrl, { params });
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const createAdjustmentConfigure = createAsyncThunk(
  'approvalLevel/create_adjustment_configure',
  async (payload: any, thunkAPI) => {
    const requestUrl = 'api/adjustment-configure';
    const result = await apiProcess.post<any>(requestUrl, payload);
    return result;
  },
);

export const getApproveConfigure = createAsyncThunk(
  'approvalLevel/get_approve_configure',
  async (params: {  ownerid: string, planPeriodId: string }, thunkAPI) => {
    const requestUrl = 'api/approve-configures';
    const result = await apiProcess.get<any>(requestUrl, { params });
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const saveDraftApproveAdjustedYear = createAsyncThunk(
  'approvalLevel/save_draft_approve_adjusted_year',
  async (payload:any, thunkAPI) => {
    const requestUrl = 'api/ajustments/draft-save';
    const result = await apiProcess.post<any>(requestUrl, payload);
    return result;
  },
);

export const getApproveAdjustedYear = createAsyncThunk(
  'approvalLevel/get_approve_adjusted_year',
  async (payload: any, thunkAPI) => {
    const requestUrl = '/api/ajustments/ajustment-targets';
    const result = await apiProcess.get<any>(requestUrl,
      {
        params: {
          ownerId: payload.ownerId,
          userId: payload.userId,
          planPeriodId: payload.planPeriodId
        }
      }
    );
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const ApprovalLevelSlice = createEntitySlice({
  name: 'approvalLevel',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.viewing = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, previewAjustmentSubmission, postAjustmentSubmission), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
        state.previewAjustmentSubmission = action.payload.data;
        state.previewAjustmentSubmissionLoading = false;
        state.previewAjustmentSubmissionError = null;
        state.previewAjustmentSubmissionSuccess = true;
        state.postAjustmentSubmissionLoading = false;
        state.postAjustmentSubmissionError = null;
        state.postAjustmentSubmissionSuccess = true;
      })
      .addMatcher(isPending(getEntities), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.entities = [];
      })
      .addMatcher(isPending(getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.viewing = true;
        state.entity = {};
      })
      .addMatcher(isPending(createEntity, updateEntity, deleteEntity, previewAjustmentSubmission), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
        state.previewAjustmentSubmissionLoading = false;
        state.previewAjustmentSubmissionError = null;
        state.previewAjustmentSubmissionSuccess = false;
        state.postAjustmentSubmissionLoading = false;
        state.postAjustmentSubmissionError = null;
        state.postAjustmentSubmissionSuccess = false;
      })
      .addMatcher(isPending(createAdjustmentConfigure), state => {
        state.loadingCreateAdjustmentConfigure = true;
      })
      .addMatcher(isFulfilled(createAdjustmentConfigure), (state, action) => {
        state.loadingCreateAdjustmentConfigure = false;
        state.createAdjustmentConfigureError = null;
      })
      .addMatcher(isRejected(createAdjustmentConfigure), (state, action) => {
        state.loadingCreateAdjustmentConfigure = false;
        state.createAdjustmentConfigureError = action.error;
      })
      .addMatcher(isFulfilled(getAdjustmentConfigure, getApproveConfigure, getApproveAdjustedYear), (state, action) => {
        state.adjustmentConfigure = action.payload.data;
        state.approveConfigure = action.payload.data;
        state.approveAdjustedYear = action.payload.data;
        state.loadingAdjustmentConfigure = false;
        state.loadingApproveConfigure = false;
        state.loadingApproveAdjustedYear = false;
      })
      .addMatcher(isPending(previewAjustmentSubmission), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
        state.previewAjustmentSubmissionLoading = true;
        state.previewAjustmentSubmissionError = null;
        state.previewAjustmentSubmissionSuccess = false;
      })
      .addMatcher(isPending(postAjustmentSubmission), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
        state.postAjustmentSubmissionLoading = true;
        state.postAjustmentSubmissionError = null;
        state.postAjustmentSubmissionSuccess = false;
      })
      .addMatcher(isPending(getTypeSelectboxApprovalLevel), state => {
        state.loadingTypeSelectbox = true;
        state.typeSelectbox = [];
      })
      .addMatcher(isFulfilled(getTypeSelectboxApprovalLevel), (state, action) => {
        state.typeSelectbox = action.payload.data;
        state.loadingTypeSelectbox = false;
      })
      .addMatcher(isRejected(getTypeSelectboxApprovalLevel), state => {
        state.loadingTypeSelectbox = false;
        state.typeSelectbox = [];
      })
      .addMatcher(isFulfilled(getAdjustmentConfigure, getApproveConfigure, getApproveAdjustedYear), (state, action) => {
        state.adjustmentConfigure = action.payload.data;
        state.approveConfigure = action.payload.data;
        state.approveAdjustedYear = action.payload.data;
        state.loadingAdjustmentConfigure = false;
        state.loadingApproveConfigure = false;
        state.loadingApproveAdjustedYear = false;
      })
      .addMatcher(isPending(getAdjustmentConfigure, getApproveConfigure, getApproveAdjustedYear), state => {
        state.loadingAdjustmentConfigure = true;
        state.loadingApproveConfigure = true;
        state.loadingApproveAdjustedYear = true;
      })
      .addMatcher(isRejected(getAdjustmentConfigure, getApproveConfigure, getApproveAdjustedYear), state => {
        state.loadingAdjustmentConfigure = false;
        state.loadingApproveConfigure = false;
        state.loadingApproveAdjustedYear = false;
      })
  },
});

export const { reset } = ApprovalLevelSlice.actions;

// Reducer
export default ApprovalLevelSlice.reducer;
